import React, { useState, useMemo, useEffect, useRef } from "react";
import { Modal, Form, Button } from "react-bootstrap";
import Select from "react-select";
import {
  Crown,
  Users,
  Heart,
  Sparkles,
  Wrench,
  Church,
  HeartHandshake,
  Plus,
  Trash2,
  X,
  UserPlus,
  PlusCircle,
  RotateCw,
  Check,
} from "lucide-react";
import { toast } from "react-toastify";
import DeleteModel from "./DeleteModel";
import UpdateCouplePopUp from "../pages/common/UpdateCouplePopUp";
import UpdatePriestPopUp from "../pages/Priest/UpdatePriestPopUp";
import PriestApi from "../api/priestApi";
import AuthApi from "../api/authApi";
import AdminApi from "../api/admin";
import { formatCoupleOptionLabel } from "../utils/coupleLabels";
import "../assets/css/rosterActionModals.css";
import "../assets/css/rosterFilterActionModal.css";

const BLANK_COUPLE = {
  primaryKey: "",
  hisFirstName: "",
  hisLastName: "",
  hisEmail: "",
  hisMobile: "",
  herFirstName: "",
  herLastName: "",
  herEmail: "",
  herMobile: "",
  city: "",
  state: "",
  zip: "",
  notes: "",
  parishId: "",
  imageUrl: "",
  under_age_35: false,
  address: "",
  allergies: "",
  anniversary_date: "",
  emergency_name1: "",
  emergency_phone1: "",
  emergency_relationship1: "",
  emergency_name2: "",
  emergency_phone2: "",
  emergency_relationship2: "",
  hear_us: [],
  referral_from_a_friend_or_family_member: "",
};

const BLANK_PRIEST = {
  id: "",
  parishId: null,
  title: "",
  firstName: "",
  lastName: "",
  email: "",
  city: "",
  state: "",
  zip: "",
  imageUrl: "",
  address: "",
};

function RosterFilterActionModal({
  show,
  handleClose,
  filterType,
  label,
  retreatId,
  isTemp,
  allRoasters = [],
  allCoupleData = [],
  serviceDataList = [],
  retreatRoastApi,
  serviceApi,
  spritualDirector,
  parishList = [],
  setParishList,
  userData,
  retreatDetail,
  onRefreshCouples,
  onRefreshServices,
  onSuccess,
}) {
  const adminApi = useMemo(() => new AdminApi(), []);
  const authApi = useMemo(() => new AuthApi(), []);
  const priestApi = useMemo(() => new PriestApi(), []);

  // Form selections
  // Array of selected couple IDs (strings or numbers) for multi-row
  const [coupleRows, setCoupleRows] = useState([""]);
  const [selectedServiceId, setSelectedServiceId] = useState("");
  const [selectedPriestId, setSelectedPriestId] = useState("");
  const [priestsList, setPriestsList] = useState([]);
  const [loadingPriests, setLoadingPriests] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [currentDirector, setCurrentDirector] = useState(spritualDirector || null);

  // Sub-modal for creating a couple
  const [showCoupleModal, setShowCoupleModal] = useState(false);
  const [coupleFormData, setCoupleFormData] = useState({ ...BLANK_COUPLE });
  const [coupleOldValues, setCoupleOldValues] = useState({});
  const [targetRowIndexForNewCouple, setTargetRowIndexForNewCouple] = useState(0);

  // Sub-modal for creating a priest
  const [showPriestModal, setShowPriestModal] = useState(false);
  const [priestFormData, setPriestFormData] = useState({ ...BLANK_PRIEST });
  const [priestOldValues, setPriestOldValues] = useState({});

  // Sub-modal for quick service creation
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [newServiceName, setNewServiceName] = useState("");
  const [newSpanishServiceName, setNewSpanishServiceName] = useState("");
  const [savingService, setSavingService] = useState(false);

  // Delete confirmation
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pendingRemoveItem, setPendingRemoveItem] = useState(null);
  const [removing, setRemoving] = useState(false);

  // Dedicated assigned list fetched per filterType (avoids GROUP BY coupleId collapsing multi-service couples)
  const [filteredAssigned, setFilteredAssigned] = useState([]);
  const [loadingAssigned, setLoadingAssigned] = useState(false);
  const [assignedFetched, setAssignedFetched] = useState(false);

  // Load priest list if spiritualDirector filter
  const fetchPriests = async () => {
    setLoadingPriests(true);
    try {
      const res = await priestApi.getAllPriests({ pageNo: 1, size: 200, export: true });
      if (res && res.success && res.data) {
        setPriestsList(res.data);
      }
    } catch (err) {
      console.error("Failed to fetch priests", err);
    } finally {
      setLoadingPriests(false);
    }
  };

  // Fetch the accurately-filtered assigned list for the current filterType.
  // Using a dedicated API call with filterBy avoids the GROUP BY coupleId
  // issue that collapses multi-service couples (e.g. RTP + another service).
  const fetchAssignedList = async () => {
    if (!retreatRoastApi || !retreatId || filterType === "spiritualDirector") return;
    setLoadingAssigned(true);
    setAssignedFetched(false);
    try {
      const res = await retreatRoastApi.getRetreatRoastData({
        retreatId,
        isTemp,
        filterBy: filterType,
        export: true,
      });
      if (res && res.success && Array.isArray(res.data)) {
        setFilteredAssigned(res.data);
      } else {
        setFilteredAssigned([]);
      }
    } catch (err) {
      console.error("Failed to fetch assigned list for", filterType, err);
      setFilteredAssigned([]);
    } finally {
      setLoadingAssigned(false);
      setAssignedFetched(true);
    }
  };

  const fetchCurrentDirector = async () => {
    try {
      const res = await retreatRoastApi.getSpritualDirectorData({
        retreatId,
        isTemp,
      });
      if (res && res.success) {
        setCurrentDirector(res.data || null);
      }
    } catch (err) {
      console.error("Failed to fetch spiritual director", err);
    }
  };

  useEffect(() => {
    setCurrentDirector(spritualDirector || null);
  }, [spritualDirector]);

  useEffect(() => {
    if (show) {
      setCoupleRows([""]);
      setSelectedServiceId("");
      setSelectedPriestId("");
      setFilteredAssigned([]);
      setAssignedFetched(false);

      // Fetch dedicated filtered assigned list (accurate, not collapsed by GROUP BY coupleId)
      if (filterType !== "spiritualDirector") {
        fetchAssignedList();
      }

      if (filterType === "spiritualDirector") {
        fetchPriests();
        fetchCurrentDirector();
      }

      if (filterType === "service" && (!serviceDataList || serviceDataList.length === 0)) {
        if (onRefreshServices) onRefreshServices();
      }

      if (!allCoupleData || allCoupleData.length === 0) {
        if (onRefreshCouples) onRefreshCouples();
      }
    }
  }, [show, filterType]);

  // Modal Meta Configuration
  const meta = useMemo(() => {
    switch (filterType) {
      case "leadCouple":
        return {
          title: "Assign Lead Couple",
          kicker: "Retreat Leadership",
          icon: <Crown size={22} />,
          markClass: "ram-mark--lead",
          helpText:
            "Select one or more couples to assign as Lead Couple for this Retreat only. You can add multiple couples using the + button.",
          badgeLabel: "Lead Couple",
          badgeClass: "ram-badge-chip--lead",
        };
      case "team":
        return {
          title: "Assign Team Couple",
          kicker: "Retreat Team",
          icon: <Users size={22} />,
          markClass: "ram-mark--team",
          helpText:
            "Select couples to add to the Team for this Retreat. Click + to add multiple couples.",
          badgeLabel: "Team",
          badgeClass: "ram-badge-chip",
        };
      case "retreatant":
        return {
          title: "Assign Retreatant Couple",
          kicker: "Retreat Attendees",
          icon: <Heart size={22} />,
          markClass: "ram-mark--retreatant",
          helpText:
            "Select couples to register as Retreatants for this Retreat.",
          badgeLabel: "Retreatant",
          badgeClass: "ram-badge-chip",
        };
      case "spiritualMentor":
        return {
          title: "Assign Spiritual Mentor",
          kicker: "Retreat Mentors",
          icon: <Sparkles size={22} />,
          markClass: "ram-mark--mentor",
          helpText:
            "Select couples to assign as Spiritual Mentors for this Retreat.",
          badgeLabel: "Spiritual Mentor",
          badgeClass: "ram-badge-chip--mentor",
        };
      case "rtp":
        return {
          title: "Assign RTP Couple",
          kicker: "Return To Purpose",
          icon: <HeartHandshake size={22} />,
          markClass: "ram-mark--rtp",
          helpText:
            "Select couples to assign to the RTP service for this Retreat.",
          badgeLabel: "RTP",
          badgeClass: "ram-badge-chip",
        };
      case "service":
        return {
          title: "Assign Services",
          kicker: "Retreat Service",
          icon: <Wrench size={22} />,
          markClass: "ram-mark--service",
          helpText:
            "Select a couple and choose the service to assign to them for this Retreat. You can also create a new couple or service directly.",
          badgeLabel: "Service",
          badgeClass: "ram-badge-chip--service",
        };
      case "spiritualDirector":
        return {
          title: "Spiritual Director (Priest)",
          kicker: "Retreat Clergy",
          icon: <Church size={22} />,
          markClass: "ram-mark--director",
          helpText:
            "Each retreat has one Spiritual Director. Select a Priest from the list, or add a New Priest if not listed.",
          badgeLabel: "Spiritual Director",
          badgeClass: "ram-badge-chip",
        };
      default:
        return {
          title: `Assign ${label || "Role"}`,
          kicker: "Retreat Roster",
          icon: <Users size={22} />,
          markClass: "ram-mark--team",
          helpText: `Assign couples for ${label || "this role"}.`,
          badgeLabel: label || "Role",
          badgeClass: "ram-badge-chip",
        };
    }
  }, [filterType, label]);

  // Priest Options
  const priestOptions = useMemo(() => {
    return priestsList
      .map((p) => ({
        value: p.id,
        label: `${p.primaryKey ? `${p.primaryKey} \u2013 ` : ""}${p.title ? `${p.title} ` : ""}${p.firstName} ${p.lastName || ""}${p.email ? ` (${p.email})` : ""}`.trim(),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [priestsList]);

  // Service Options
  const serviceOptions = useMemo(() => {
    return (serviceDataList || [])
      .filter(
        (s) =>
          s &&
          s.service &&
          s.service !== "NULL" &&
          String(s.service).trim() !== "",
      )
      .map((s) => ({
        value: s.id,
        label: `${s.service}${s.spanishService ? ` / ${s.spanishService}` : ""}`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [serviceDataList]);

  // Helper to resolve service name
  function getServiceName(r) {
    if (r.service && r.service !== "NULL") return r.service;
    if (r.serviceId && serviceDataList) {
      const match = serviceDataList.find(
        (s) => Number(s.id) === Number(r.serviceId),
      );
      if (match) return match.service;
    }
    return "Assigned Service";
  }

  // Assigned list — use the dedicated per-filterType fetch (filteredAssigned)
  // which calls getRetreatRoastData with filterBy=filterType, so it's exact.
  // Fall back to filtering allRoasters only if the dedicated fetch hasn't resolved yet.
  const assignedList = useMemo(() => {
    // Prefer dedicated filtered fetch (accurate, not collapsed by GROUP BY coupleId)
    if (assignedFetched) {
      return filteredAssigned;
    }
    // Fallback: filter from the broader allRoasters (may miss multi-service couples)
    if (!allRoasters || allRoasters.length === 0) return [];
    switch (filterType) {
      case "leadCouple":
        return allRoasters.filter((r) => r.mainRole === "Lead");
      case "spiritualMentor":
        return allRoasters.filter((r) => r.mainRole === "Spiritual Mentor");
      case "team":
        return allRoasters.filter(
          (r) =>
            r.attendeeType === "Team" &&
            r.mainRole !== "Lead" &&
            r.mainRole !== "Spiritual Mentor" &&
            !r.priestId,
        );
      case "retreatant":
        return allRoasters.filter((r) => r.attendeeType === "Retreatant");
      case "rtp":
        return allRoasters.filter((r) => r.service === "RTP" || Number(r.serviceId) === 1);
      case "service":
        return allRoasters.filter(
          (r) =>
            (r.service != null && r.service !== "" && r.service !== "NULL") ||
            (r.serviceId != null &&
              r.serviceId !== "" &&
              r.serviceId !== 0 &&
              r.serviceId !== "0"),
        );
      default:
        return [];
    }
  }, [assignedFetched, filteredAssigned, allRoasters, filterType]);

  // Build label for roster item
  function buildRosterCoupleLabel(r) {
    const pk = r.primaryKey ? `${r.primaryKey} \u2013 ` : "";
    const his = [r.hisLastName, r.hisFirstName].filter(Boolean).join(", ");
    const her = r.herFirstName || "";
    return `${pk}${his}${her ? ` & ${her}` : ""}`;
  }

  // Row builder handlers for multi-couple
  const handleCoupleChange = (index, selectedOption) => {
    const next = [...coupleRows];
    next[index] = selectedOption ? selectedOption.value : "";
    setCoupleRows(next);
  };

  const handleAddCoupleRow = () => {
    setCoupleRows((prev) => [...prev, ""]);
  };

  const handleRemoveCoupleRow = (index) => {
    if (coupleRows.length === 1) {
      setCoupleRows([""]);
      return;
    }
    setCoupleRows((prev) => prev.filter((_, i) => i !== index));
  };

  // Open "Create Couple" popup
  const handleOpenCreateCouple = async (rowIndex = 0) => {
    setTargetRowIndexForNewCouple(rowIndex);
    try {
      const res = await authApi.checkPKCouple();
      const nextPk =
        res?.success && res.data != null ? Number(res.data) + 1 : 1001;
      setCoupleFormData({ ...BLANK_COUPLE, primaryKey: nextPk });
      setCoupleOldValues({});
      setShowCoupleModal(true);
    } catch (err) {
      setCoupleFormData({ ...BLANK_COUPLE, primaryKey: 1001 });
      setShowCoupleModal(true);
    }
  };

  // Callback when a couple is created via UpdateCouplePopUp
  const handleCoupleCreatedSuccess = async (createdCouple) => {
    if (onRefreshCouples) {
      await onRefreshCouples();
    }
    const coupleId = createdCouple?.id;
    if (coupleId) {
      if (filterType === "service") {
        setCoupleRows([coupleId]);
      } else {
        setCoupleRows((prev) => {
          const next = [...prev];
          const idx =
            targetRowIndexForNewCouple < next.length
              ? targetRowIndexForNewCouple
              : next.length - 1;
          next[idx] = coupleId;
          return next;
        });
      }
      toast.success("New couple created and selected!");
    }
    setShowCoupleModal(false);
  };

  // Open "Create Priest" popup
  const handleOpenCreatePriest = async () => {
    try {
      const res = await priestApi.checkPKPriest();
      const nextPk = res?.success && res.data != null ? res.data : "P101";
      setPriestFormData({ ...BLANK_PRIEST, primaryKey: nextPk });
      setPriestOldValues({});
      setShowPriestModal(true);
    } catch (err) {
      setPriestFormData({ ...BLANK_PRIEST, primaryKey: "P101" });
      setShowPriestModal(true);
    }
  };

  // Callback when a priest is created via UpdatePriestPopUp
  const handlePriestCreatedSuccess = async (createdPriest) => {
    await fetchPriests();
    const priestId = createdPriest?.id;
    if (priestId) {
      setSelectedPriestId(priestId);
      toast.success("New Spiritual Director created and selected!");
    }
    setShowPriestModal(false);
  };

  // Quick Service Creation
  const handleSaveNewService = async (e) => {
    if (e) e.preventDefault();
    const trimmed = (newServiceName || "").trim();
    if (!trimmed) {
      toast.error("Please enter a service name.");
      return;
    }
    setSavingService(true);
    try {
      const res = await serviceApi.addService({
        service: trimmed,
        spanishService: (newSpanishServiceName || "").trim() || null,
      });
      if (res && res.success) {
        toast.success("Service created successfully!");
        setNewServiceName("");
        setNewSpanishServiceName("");
        setShowServiceModal(false);
        if (onRefreshServices) {
          await onRefreshServices();
        }
        if (res.data?.id) {
          setSelectedServiceId(res.data.id);
        }
      } else {
        toast.error(res?.msg || "Failed to create service.");
      }
    } catch (err) {
      toast.error(err?.message || "Error creating service.");
    } finally {
      setSavingService(false);
    }
  };

  // Assign Submit
  const handleAssignSubmit = async () => {
    if (submitting) return;

    if (filterType === "spiritualDirector") {
      if (!selectedPriestId) {
        toast.error("Please select a Spiritual Director (Priest).");
        return;
      }
      setSubmitting(true);
      try {
        const res = await retreatRoastApi.assignRosterRole({
          retreatId,
          isTemp,
          filterType: "spiritualDirector",
          priestId: selectedPriestId,
        });
        if (res && res.success) {
          toast.success(res.msg || "Spiritual Director assigned successfully!");
          setSelectedPriestId("");
          if (onSuccess) onSuccess();
        } else {
          toast.error(res?.msg || "Failed to assign Spiritual Director.");
        }
      } catch (err) {
        toast.error(err?.message || "An error occurred.");
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (filterType === "service") {
      const coupleId = coupleRows[0];
      if (!coupleId) {
        toast.error("Please select a couple.");
        return;
      }
      if (!selectedServiceId) {
        toast.error("Please select a service.");
        return;
      }
      setSubmitting(true);
      try {
        const res = await retreatRoastApi.assignRosterRole({
          retreatId,
          isTemp,
          filterType: "service",
          coupleIds: [coupleId],
          serviceId: selectedServiceId,
        });
        if (res && res.success) {
          toast.success(res.msg || "Service assigned successfully!");
          setCoupleRows([""]);
          setSelectedServiceId("");
          fetchAssignedList();
          if (onSuccess) onSuccess();
        } else {
          toast.error(res?.msg || "Failed to assign service.");
        }
      } catch (err) {
        toast.error(err?.message || "An error occurred.");
      } finally {
        setSubmitting(false);
      }
      return;
    }

    // Role-based multi-couple
    const validIds = coupleRows.filter(Boolean);
    if (validIds.length === 0) {
      toast.error("Please select at least one couple.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await retreatRoastApi.assignRosterRole({
        retreatId,
        isTemp,
        filterType,
        coupleIds: validIds,
      });
      if (res && res.success) {
        toast.success(res.msg || "Couples assigned successfully!");
        setCoupleRows([""]);
        fetchAssignedList();
        if (onSuccess) onSuccess();
      } else {
        toast.error(res?.msg || "Failed to assign couples.");
      }
    } catch (err) {
      toast.error(err?.message || "An error occurred.");
    } finally {
      setSubmitting(false);
    }
  };

  // Remove / Unassign handling
  const confirmRemoveRoster = (item) => {
    setPendingRemoveItem(item);
    setShowDeleteModal(true);
  };

  const handleConfirmRemove = async () => {
    if (!pendingRemoveItem || removing) return;
    setRemoving(true);
    setShowDeleteModal(false);
    try {
      const res = await retreatRoastApi.unassignRosterRole({
        retreatId,
        isTemp,
        rosterId: pendingRemoveItem.rosterId,
        filterType,
        action: "unassign",
      });
      if (res && res.success) {
        toast.success(res.msg || "Removed successfully!");
        if (filterType === "spiritualDirector") {
          setCurrentDirector(null);
        }
        setPendingRemoveItem(null);
        fetchAssignedList();
        if (onSuccess) onSuccess();
      } else {
        toast.error(res?.msg || "Failed to remove.");
      }
    } catch (err) {
      toast.error(err?.message || "An error occurred while removing.");
    } finally {
      setRemoving(false);
    }
  };

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        centered
        className="ram-modal"
        dialogClassName="ram-dialog ram-dialog-wide"
        contentClassName="ram-content"
      >
        <div className="ram-shell">
          <header className="ram-header">
            <div className={`ram-mark ${meta.markClass}`} aria-hidden="true">
              {meta.icon}
            </div>
            <div className="ram-header-copy">
              <p className="ram-kicker">{meta.kicker}</p>
              <h2>{meta.title}</h2>
              <p>{meta.helpText}</p>
            </div>
            <button
              type="button"
              className="ram-close"
              onClick={handleClose}
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </header>

          <div className="ram-body">
            {/* FORM PANEL */}
            <section className="ram-panel">
              {filterType === "spiritualDirector" ? (
                <>
                  {currentDirector ? (
                    <div className="ram-current-director-container">
                      <div className="ram-director-card-modern">
                        <div className="ram-director-card-left">
                          {currentDirector.imageUrl ? (
                            <img
                              src={currentDirector.imageUrl}
                              alt=""
                              className="ram-director-avatar-img"
                            />
                          ) : (
                            <div className="ram-director-avatar-placeholder">
                              <Church size={24} />
                            </div>
                          )}
                          <div className="ram-director-info">
                            <span className="ram-director-title-badge">
                              Current Spiritual Director
                            </span>
                            <h4 className="ram-director-name">
                              {[
                                currentDirector.title,
                                currentDirector.firstName,
                                currentDirector.lastName,
                              ]
                                .filter(Boolean)
                                .join(" ")}
                            </h4>
                            <div className="ram-director-meta">
                              {currentDirector.parish?.parish && (
                                <span className="ram-director-meta-item">
                                  <Church size={14} />
                                  {currentDirector.parish.parish}
                                </span>
                              )}
                              {currentDirector.email && (
                                <span className="ram-director-meta-item">
                                  <i className="mdi mdi-email-outline" />
                                  {currentDirector.email}
                                </span>
                              )}
                              {currentDirector.primaryKey && (
                                <span className="ram-director-meta-badge">
                                  ID: {currentDirector.primaryKey}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          className="ram-btn-danger-outline"
                          title="Remove Spiritual Director from retreat"
                          onClick={() =>
                            confirmRemoveRoster({
                              rosterId: currentDirector.rosterId,
                              title: [
                                currentDirector.title,
                                currentDirector.firstName,
                                currentDirector.lastName,
                              ]
                                .filter(Boolean)
                                .join(" "),
                            })
                          }
                        >
                          <Trash2 size={16} />
                          Remove Spiritual Director
                        </button>
                      </div>
                      <div className="ram-director-note">
                        <small className="text-muted">
                          To assign a different priest, please remove the current Spiritual Director first. Each retreat can only have one Spiritual Director.
                        </small>
                      </div>
                    </div>
                  ) : (
                    <div className="ram-director-assign-box">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <label className="fw-semibold text-secondary small mb-0">
                          Select Spiritual Director (Priest)
                        </label>
                        <button
                          type="button"
                          className="ram-action-btn-secondary is-highlight"
                          onClick={handleOpenCreatePriest}
                          title="Add a new Catholic Priest / Spiritual Director"
                        >
                          <UserPlus size={15} />
                          Add Spiritual Director
                        </button>
                      </div>

                      <div className="d-flex gap-2 align-items-center">
                        <div style={{ flex: 1 }}>
                          <Select
                            className="ram-select"
                            classNamePrefix="ram-select"
                            placeholder="Search priest by name, ID or email..."
                            isSearchable
                            isClearable
                            isLoading={loadingPriests}
                            options={priestOptions}
                            value={
                              priestOptions.find(
                                (p) =>
                                  String(p.value) === String(selectedPriestId),
                              ) || null
                            }
                            onChange={(opt) =>
                              setSelectedPriestId(opt ? opt.value : "")
                            }
                            menuPortalTarget={
                              typeof document !== "undefined"
                                ? document.body
                                : null
                            }
                            styles={{
                              menuPortal: (base) => ({ ...base, zIndex: 3500 }),
                            }}
                          />
                        </div>
                        <button
                          type="button"
                          className={`ram-btn ram-btn-primary${submitting ? " is-busy" : ""}`}
                          onClick={handleAssignSubmit}
                          disabled={!selectedPriestId || submitting}
                        >
                          {submitting ? (
                            <span className="ram-spinner" />
                          ) : (
                            <Check size={16} />
                          )}
                          Assign
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : filterType === "service" ? (
                <>
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <label className="fw-semibold text-secondary small mb-0">
                      Assign Service to Couple
                    </label>
                    <div className="ram-quick-creators">
                      <button
                        type="button"
                        className="ram-action-btn-secondary"
                        onClick={() => handleOpenCreateCouple(0)}
                        title="Create a new couple"
                      >
                        <UserPlus size={15} />
                        Create Couple
                      </button>
                      <button
                        type="button"
                        className="ram-action-btn-secondary is-highlight"
                        onClick={() => setShowServiceModal(true)}
                        title="Create a new service"
                      >
                        <PlusCircle size={15} />
                        Create Service
                      </button>
                    </div>
                  </div>

                  <div className="row g-2 align-items-center">
                    <div className="col-12 col-md-6">
                      <label className="small text-muted mb-1">Couple</label>
                      <Select
                        className="ram-select"
                        classNamePrefix="ram-select"
                        placeholder="Search couple by name or ID..."
                        isSearchable
                        isClearable
                        options={allCoupleData}
                        value={
                          allCoupleData.find(
                            (c) => String(c.value) === String(coupleRows[0]),
                          ) || null
                        }
                        onChange={(opt) => handleCoupleChange(0, opt)}
                        menuPortalTarget={
                          typeof document !== "undefined" ? document.body : null
                        }
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 3500 }) }}
                      />
                    </div>
                    <div className="col-12 col-md-4">
                      <label className="small text-muted mb-1">Service</label>
                      <Select
                        className="ram-select"
                        classNamePrefix="ram-select"
                        placeholder="Select service..."
                        isSearchable
                        isClearable
                        options={serviceOptions}
                        value={
                          serviceOptions.find(
                            (s) =>
                              String(s.value) === String(selectedServiceId),
                          ) || null
                        }
                        onChange={(opt) =>
                          setSelectedServiceId(opt ? opt.value : "")
                        }
                        menuPortalTarget={
                          typeof document !== "undefined" ? document.body : null
                        }
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 3500 }) }}
                      />
                    </div>
                    <div className="col-12 col-md-2 d-flex align-items-end" style={{ paddingTop: 24 }}>
                      <button
                        type="button"
                        className={`ram-btn ram-btn-primary w-100${submitting ? " is-busy" : ""}`}
                        onClick={handleAssignSubmit}
                        disabled={!coupleRows[0] || !selectedServiceId || submitting}
                      >
                        {submitting ? (
                          <span className="ram-spinner" />
                        ) : (
                          <Plus size={16} />
                        )}
                        Assign
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <label className="fw-semibold text-secondary small mb-0">
                      Select Couple(s) for {meta.badgeLabel}
                    </label>
                    <button
                      type="button"
                      className="ram-action-btn-secondary is-highlight"
                      onClick={() => handleOpenCreateCouple(coupleRows.length - 1)}
                      title="Create a new couple"
                    >
                      <UserPlus size={15} />
                      Create Couple
                    </button>
                  </div>

                  <div className="ram-couple-rows">
                    {coupleRows.map((selectedId, idx) => {
                      const selectedOption =
                        allCoupleData.find(
                          (c) => String(c.value) === String(selectedId),
                        ) || null;
                      return (
                        <div key={idx} className="ram-couple-row">
                          <div className="ram-couple-select-wrap">
                            <Select
                              className="ram-select"
                              classNamePrefix="ram-select"
                              placeholder={`Select couple #${idx + 1}...`}
                              isSearchable
                              isClearable
                              options={allCoupleData}
                              value={selectedOption}
                              onChange={(opt) => handleCoupleChange(idx, opt)}
                              menuPortalTarget={
                                typeof document !== "undefined"
                                  ? document.body
                                  : null
                              }
                              styles={{
                                menuPortal: (base) => ({
                                  ...base,
                                  zIndex: 3500,
                                }),
                              }}
                            />
                          </div>
                          {coupleRows.length > 1 ? (
                            <button
                              type="button"
                              className="ram-row-remove-btn"
                              onClick={() => handleRemoveCoupleRow(idx)}
                              title="Remove row"
                              aria-label="Remove row"
                            >
                              <X size={16} />
                            </button>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>

                  <div className="ram-form-actions-bar">
                    <button
                      type="button"
                      className="ram-add-row-btn"
                      onClick={handleAddCoupleRow}
                    >
                      <Plus size={15} />
                      Add another Couple
                    </button>

                    <button
                      type="button"
                      className={`ram-btn ram-btn-primary${submitting ? " is-busy" : ""}`}
                      onClick={handleAssignSubmit}
                      disabled={!coupleRows.some(Boolean) || submitting}
                    >
                      {submitting ? (
                        <span className="ram-spinner" />
                      ) : (
                        <Check size={16} />
                      )}
                      {submitting ? "Saving..." : `Assign ${meta.badgeLabel}`}
                    </button>
                  </div>
                </>
              )}
            </section>

            {/* CURRENTLY ASSIGNED LIST */}
            {filterType !== "spiritualDirector" && (
              <section className="ram-assigned">
                <div className="ram-assigned-head">
                  <h3>Currently Assigned {meta.badgeLabel}</h3>
                  <span className="ram-count">
                    {loadingAssigned ? <span className="ram-spinner" style={{ width: 14, height: 14 }} /> : assignedList.length}
                  </span>
                </div>

                {loadingAssigned ? (
                  <div className="ram-loading-row">
                    <span className="ram-spinner ram-spinner--dark" style={{ width: 20, height: 20 }} />
                    <span>Loading assigned {meta.badgeLabel}...</span>
                  </div>
                ) : assignedList.length === 0 ? (
                  <div className="ram-empty">
                    <strong>No {meta.badgeLabel} assigned yet</strong>
                    Use the selector above to assign couples to this role.
                  </div>
                ) : (
                  <div className="ram-list">
                    <div
                      className={`ram-row is-head${filterType === "service" ? " is-service-grid" : ""
                        }`}
                    >
                      {filterType === "service" && <span>Service</span>}
                      <span>Couple</span>
                      <span>Contact</span>
                      <span className="text-end">Action</span>
                    </div>

                    {assignedList.map((r) => {
                      const label = buildRosterCoupleLabel(r);
                      const svc = getServiceName(r);
                      return (
                        <div
                          key={r.id}
                          className={`ram-row${filterType === "service" ? " is-service-grid" : ""
                            }`}
                        >
                          {filterType === "service" && (
                            <span>
                              <span className="ram-badge-chip ram-badge-chip--service">
                                {svc}
                              </span>
                            </span>
                          )}

                          <span className="ram-person">
                            <strong>{label}</strong>
                            {r.parish ? <small>{r.parish}</small> : null}
                          </span>

                          <span className="small text-muted ram-contact-cell">
                            {r.hisEmail && (
                              <div className="ram-contact-line">
                                <span className="fw-semibold text-dark">
                                  His Email:{" "}
                                </span>
                                <span>{r.hisEmail}</span>
                              </div>
                            )}
                            {r.herEmail && (
                              <div className="ram-contact-line">
                                <span className="fw-semibold text-dark">
                                  Her Email:{" "}
                                </span>
                                <span>{r.herEmail}</span>
                              </div>
                            )}
                            {!r.hisEmail && !r.herEmail && (
                              <div className="ram-contact-line">
                                {r.hisMobile || r.herMobile || "\u2014"}
                              </div>
                            )}
                          </span>

                          <span className="text-end">
                            <button
                              type="button"
                              className="ram-icon-btn ram-icon-btn--danger"
                              title={`Remove ${meta.badgeLabel}`}
                              aria-label={`Remove ${meta.badgeLabel}`}
                              onClick={() =>
                                confirmRemoveRoster({
                                  rosterId: r.id,
                                  title: label,
                                  service: svc,
                                })
                              }
                            >
                              <Trash2 size={16} />
                            </button>
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            )}
          </div>

          <footer className="ram-footer">
            <button
              type="button"
              className="ram-btn ram-btn-secondary"
              onClick={handleClose}
            >
              Close
            </button>
          </footer>
        </div>
      </Modal>

      {/* QUICK CREATE SERVICE SUB-MODAL */}
      <Modal
        show={showServiceModal}
        onHide={() => setShowServiceModal(false)}
        centered
        className="ram-modal"
        dialogClassName="ram-dialog"
        contentClassName="ram-content"
      >
        <div className="ram-shell">
          <header className="ram-header">
            <div className="ram-mark ram-mark--service">
              <Wrench size={20} />
            </div>
            <div className="ram-header-copy">
              <p className="ram-kicker">New Service</p>
              <h2>Create New Service</h2>
              <p>Add a new service to the retreat catalog.</p>
            </div>
            <button
              type="button"
              className="ram-close"
              onClick={() => setShowServiceModal(false)}
            >
              <X size={18} />
            </button>
          </header>
          <Form onSubmit={handleSaveNewService}>
            <div className="ram-body" style={{ padding: "1.25rem" }}>
              <Form.Group className="mb-3">
                <Form.Label className="small fw-semibold">Service Name *</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g. Music Ministry, Hospitality..."
                  value={newServiceName}
                  onChange={(e) => setNewServiceName(e.target.value)}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label className="small fw-semibold">
                  Spanish Service Name (Optional)
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g. Ministerio de Msica..."
                  value={newSpanishServiceName}
                  onChange={(e) => setNewSpanishServiceName(e.target.value)}
                />
              </Form.Group>
            </div>
            <footer className="ram-footer">
              <button
                type="button"
                className="ram-btn ram-btn-secondary"
                onClick={() => setShowServiceModal(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={`ram-btn ram-btn-primary${savingService ? " is-busy" : ""}`}
                disabled={savingService || !newServiceName.trim()}
              >
                {savingService ? "Saving..." : "Save Service"}
              </button>
            </footer>
          </Form>
        </div>
      </Modal>

      {/* REUSABLE UPDATE COUPLE MODAL */}
      {showCoupleModal && (
        <UpdateCouplePopUp
          showRegModal={showCoupleModal}
          setShowREgModal={setShowCoupleModal}
          setEdiUserIdClicked={() => { }}
          onHide={() => setShowCoupleModal(false)}
          closeButton={() => setShowCoupleModal(false)}
          userData={userData}
          isEditAdmin={false}
          parishList={parishList}
          setParishList={setParishList}
          ediUserIdClicked=""
          CloseCompose={() => setShowCoupleModal(false)}
          roasterCount={0}
          roastersOfCoupleList={[]}
          getAllCouples={async () => {
            if (onRefreshCouples) await onRefreshCouples();
          }}
          data={coupleFormData}
          setData={setCoupleFormData}
          oldValues={coupleOldValues}
          setOldValues={setCoupleOldValues}
          isTempUpload={false}
          setIsTempUpload={() => { }}
          retreatDetail={retreatDetail}
          onSuccess={handleCoupleCreatedSuccess}
        />
      )}

      {/* REUSABLE UPDATE PRIEST MODAL */}
      {showPriestModal && (
        <UpdatePriestPopUp
          showRegModal={showPriestModal}
          setShowREgModal={setShowPriestModal}
          setEdiUserIdClicked={() => { }}
          onHide={() => setShowPriestModal(false)}
          closeButton={() => setShowPriestModal(false)}
          userData={userData}
          isEditAdmin={false}
          parishList={parishList}
          setParishList={setParishList}
          ediUserIdClicked=""
          CloseCompose={() => setShowPriestModal(false)}
          roasterCount={0}
          roastersOfPriestList={[]}
          getAllPriests={fetchPriests}
          data={priestFormData}
          setData={setPriestFormData}
          oldValues={priestOldValues}
          setOldValues={setPriestOldValues}
          isTempUpload={false}
          setIsTempUpload={() => { }}
          retreatDetail={retreatDetail}
          redirect={false}
          onSuccess={handlePriestCreatedSuccess}
        />
      )}

      {/* DELETE / UNASSIGN CONFIRMATION */}
      {showDeleteModal && (
        <DeleteModel
          show={showDeleteModal}
          handleClose={() => {
            setShowDeleteModal(false);
            setPendingRemoveItem(null);
          }}
          DeleteData={handleConfirmRemove}
          content={
            <div className="p-3 border rounded-3 bg-light shadow-sm text-start">
              <h5 className="fw-bold mb-3 text-center">
                Remove {meta.badgeLabel}
              </h5>
              {filterType === "service" ? (
                <>
                  {pendingRemoveItem?.service && (
                    <div className="mb-2">
                      <span className="fw-bold">Service:</span>{" "}
                      <span className="text-secondary">
                        {pendingRemoveItem.service}
                      </span>
                    </div>
                  )}
                  <div className="mb-2">
                    <span className="fw-bold">Couple:</span>{" "}
                    <span className="text-secondary">
                      {pendingRemoveItem?.title || "Selected Couple"}
                    </span>
                  </div>
                  <p className="small text-muted mt-2 mb-0">
                    Are you sure you want to unassign this service for this retreat?
                  </p>
                </>
              ) : filterType === "spiritualDirector" ? (
                <>
                  <div className="mb-2">
                    <span className="fw-bold">Priest:</span>{" "}
                    <span className="text-secondary">
                      {pendingRemoveItem?.title}
                    </span>
                  </div>
                  <p className="small text-muted mt-2 mb-0">
                    Are you sure you want to remove this Spiritual Director from this retreat?
                  </p>
                </>
              ) : (
                <>
                  <div className="mb-2">
                    <span className="fw-bold">Couple:</span>{" "}
                    <span className="text-secondary">
                      {pendingRemoveItem?.title}
                    </span>
                  </div>
                  <div className="mb-2">
                    <span className="fw-bold">Role:</span>{" "}
                    <span className="text-secondary">{meta.badgeLabel}</span>
                  </div>
                  <p className="small text-muted mt-2 mb-0">
                    Are you sure you want to remove this couple from{" "}
                    <strong>{meta.badgeLabel}</strong> for this retreat?
                  </p>
                </>
              )}
            </div>
          }
        />
      )}
    </>
  );
}

export default RosterFilterActionModal;
